import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, MapPin, ShieldCheck, Hotel, CalendarDays, ExternalLink, ArrowRight, Star, AlertTriangle, Maximize2, Sparkles } from 'lucide-react';
import { destinations, hotels } from '../data';
import { tourAreas } from '../tourData';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { buildDestinationSchema, buildBreadcrumbSchema, buildOrganizationSchema } from '../lib/schema';

const renderFormattedText = (text) => {
  if (!text || typeof text !== 'string') return text;
  if (!text.includes('**')) return text;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const HotelCard = ({ hotel }) => {
  const { language, translateObject, getLocalizedPath } = useLanguage();
  const containerRef = useRef(null);
  const [hasSpace, setHasSpace] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setHasSpace(entry.contentRect.width >= 379);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const translatedHotel = translateObject(hotel);

  return (
    <Link 
      to={getLocalizedPath('hotel', language, { hotelId: hotel.id })}
      className="group flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 w-full min-w-0"
    >
      <div ref={containerRef} className="md:w-2/5 relative h-64 md:h-auto overflow-hidden">
        <img 
          src={translatedHotel.image} 
          alt={translatedHotel.name} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {translatedHotel.isNew && (
          <div className="absolute top-4 left-4 z-20 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white font-black text-xs uppercase px-3.5 py-1.5 rounded-full shadow-lg tracking-widest flex items-center gap-1.5 border border-amber-200/50 animate-pulse">
            <Sparkles size={13} className="fill-white shrink-0" />
            {language === 'da' ? 'NYHED' : 'NEW'}
          </div>
        )}
        <div className={`absolute ${translatedHotel.isNew ? 'top-14 left-4' : 'top-4 left-4'} z-10 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-emerald-900 uppercase`}>
          {translatedHotel.area}
        </div>

        {translatedHotel.imageBadge && (
          <div className="absolute top-4 right-4 z-20 bg-emerald-950/85 text-white backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider shadow-lg border border-emerald-500/40">
            {translatedHotel.imageBadge}
          </div>
        )}
      </div>
      <div className="md:w-3/5 p-6 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex gap-0.5 mb-1">
                {[...Array(translatedHotel.stars || 0)].map((_, i) => <Star key={i} size={12} className="fill-yellow-400 text-yellow-400" />)}
              </div>
              <h4 className="text-2xl font-serif font-bold text-gray-900 group-hover:text-emerald-700 transition-colors break-words">
                {translatedHotel.name}
              </h4>
            </div>
            <div className="bg-emerald-50/60 border border-emerald-100/80 px-5 py-3.5 rounded-2xl text-right flex flex-col items-end justify-center shrink-0 sm:min-w-[140px] shadow-sm">
              <p className="text-2xl font-sans font-extrabold text-emerald-950 leading-none">
                <span className="text-sm font-normal text-emerald-700 mr-1.5">
                  {language === 'da' ? 'Fra' : 'From'}
                </span>
                {translatedHotel.priceFrom.toLocaleString('da-DK')},-
              </p>
              <p className="text-[10px] text-emerald-700 font-bold uppercase mt-1.5">
                {language === 'da' ? 'pr. pers.' : 'per person'}
              </p>
            </div>
          </div>
          <p className="text-slate-600 line-clamp-3 mb-2 break-words [overflow-wrap:anywhere]">
            {translatedHotel.description}
          </p>
          <p className="text-[10px] text-slate-400 italic mb-4">
            {language === 'da' 
              ? '* Prisen er en fra-pris og afhænger blandt andet af afrejsedato, rejseperiode, værelsestype, ledige pladser og eventuelle særlige behov. Kontakt os for et konkret tilbud på din rejse.'
              : '* The price is a starting price and depends on departure date, travel period, room type, availability, and any special needs. Contact us for a specific quote.'}
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {translatedHotel.features.slice(0, 3).map((f, i) => (
              <span key={i} className="text-[10px] bg-emerald-50 text-emerald-800 px-2 py-1 rounded uppercase font-bold tracking-tighter">
                {f}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center text-emerald-700 font-bold text-sm">
          {language === 'da' ? 'Se detaljer og priser' : 'See details and prices'}
          <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

const DestinationDetailPage = () => {
  const { id } = useParams();
  const { language, translateObject, getLocalizedPath } = useLanguage();
  
  const rawDestination = destinations.find(d => d.id === id);
  const destination = translateObject(rawDestination);

  // Find detailed hotels for this destination
  const detailedHotels = hotels
    .filter(h => rawDestination?.hotels?.includes(h.id))
    .map(h => translateObject(h));

  // Find tour areas for this destination
  const destinationAreas = tourAreas
    .filter(area => area.destinationId === rawDestination?.id)
    .map(area => translateObject(area));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!destination) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <h2 className="text-2xl font-serif mb-4">
            {language === 'da' ? 'Destinationen blev ikke fundet' : 'Destination not found'}
          </h2>
          <Link 
            to={getLocalizedPath('destinations', language)} 
            className="text-emerald-900 underline flex items-center gap-2 justify-center"
          >
            <ArrowLeft size={16} />
            {language === 'da' ? 'Tilbage til destinationer' : 'Back to destinations'}
          </Link>
        </div>
      </div>
    );
  }

  const destinationCanonicalPath = getLocalizedPath('destination', language, { id: destination.id });
  const breadcrumbs = [
    {
      name: language === 'da' ? 'Forside' : 'Home',
      url: getLocalizedPath('home', language)
    },
    {
      name: language === 'da' ? 'Handicapvenlige feriedestinationer' : 'Accessible holiday destinations',
      url: getLocalizedPath('destinations', language)
    },
    {
      name: destination.title,
      url: destinationCanonicalPath
    }
  ];

  const destinationSchema = buildDestinationSchema({
    destination,
    url: destinationCanonicalPath,
    language
  });

  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs, destinationCanonicalPath);
  const organizationSchema = buildOrganizationSchema({ language });

  return (
    <div className="bg-stone-50 min-h-screen pb-20 relative">
      <SEO 
        title={language === 'da' ? `Handicapvenlig ferie på ${destination.title}` : `Accessible holiday in ${destination.title}`} 
        description={language === 'da' ? `Oplev en skræddersyet, handicapvenlig ferie på ${destination.title} med Alora Rejser. Vi tilbyder barrierefrie hoteller, tilgængelige udflugter og trygge rammer for kørestolsbrugere.` : `Experience a tailored, wheelchair friendly holiday in ${destination.title} with Alora Rejser. We offer barrier-free hotels, accessible excursions, and safe surroundings for wheelchair users.`}
        ogImage={destination.image}
        schemaMarkup={[organizationSchema, destinationSchema, breadcrumbSchema]}
      />
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img 
          src={destination.image} 
          alt={destination.title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center pt-20 md:pt-24">
          <div className="text-center text-white px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-7xl font-serif font-bold mb-4 drop-shadow-lg italic">
                {destination.title}
              </h1>
              <p className="text-lg md:text-2xl italic font-light drop-shadow-md max-w-3xl mx-auto">
                {destination.description}
              </p>
            </motion.div>
          </div>
        </div>
        
        <Link 
          to={getLocalizedPath('destinations', language)} 
          className="absolute top-24 left-8 bg-white/90 backdrop-blur-sm text-emerald-900 p-3 rounded-full hover:bg-white transition-colors shadow-lg z-10"
        >
          <ArrowLeft size={24} />
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-2 space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-[2rem] p-8 md:p-12 shadow-sm border border-emerald-50"
            >
              <h2 className="text-3xl font-serif font-bold text-emerald-900 mb-6 italic text-center md:text-left">
                {destination.description}
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-8 whitespace-pre-line">
                {renderFormattedText(destination.longDescription)}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-bold flex items-center gap-2 text-emerald-900">
                    <ShieldCheck className="text-emerald-700" />
                    {language === 'da' ? 'Højdepunkter' : 'Highlights'}
                  </h3>
                  <ul className="space-y-3">
                    {destination.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-600">
                        <Check size={18} className="text-emerald-600 shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {destination.legacyHotels && destination.legacyHotels.length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-serif font-bold flex items-center gap-2 text-emerald-900">
                      <Hotel className="text-emerald-700" />
                      {language === 'da' ? 'Øvrige overnatningsmuligheder' : 'Other accommodation options'}
                    </h3>
                    <div className="space-y-4">
                      {destination.legacyHotels.map((group, idx) => (
                        <div key={idx}>
                          <p className="font-bold text-xs text-emerald-800 uppercase tracking-wider mb-2">{group.area}</p>
                          <ul className="space-y-1">
                            {group.names.map((name, nIdx) => (
                              <li key={nIdx} className="text-slate-600 text-sm flex items-center gap-2">
                                <span className="w-1 h-1 bg-emerald-300 rounded-full"></span>
                                {name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Flot, mindre link til tilgængelighed og praktisk information (Placeret efter højdepunkter) */}
              <div className="p-4 bg-[#99A18C]/10 rounded-2xl border border-[#99A18C]/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-[#99A18C]/15 transition-all group">
                <div className="flex items-start sm:items-center gap-3">
                  <span className="text-xl shrink-0">♿</span>
                  <div>
                    <h4 className="font-serif font-bold text-[#99A18C] text-base">
                      {language === 'da' ? 'tilgængelighed, før under og efter din rejse' : 'accessibility, before, during, and after your trip'}
                    </h4>
                    <p className="text-xs text-slate-600">
                      {language === 'da' ? 'Læs om vores fokus på praktisk information og tilgængelighedsløsninger.' : 'Read about our focus on practical information and accessibility solutions.'}
                    </p>
                  </div>
                </div>
                <Link 
                  to={getLocalizedPath('practical', language)} 
                  className="inline-flex items-center gap-2 text-[#99A18C] font-bold text-xs shrink-0 hover:underline"
                >
                  {language === 'da' ? 'Læs mere her' : 'Read more here'}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Kort og Nationalpark Sommercard sektion for Østrig */}
              {destination.id === 'oestrig' && (
                <div className="space-y-6 mt-8">
                  {/* Nationalpark Sommercard Card */}
                  <div className="p-6 md:p-8 bg-stone-50 rounded-3xl border border-stone-200/60">
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                      <div className="md:w-1/2 space-y-4">
                        <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider">
                          {language === 'da' ? 'Inkluderet i opholdet' : 'Included in your stay'}
                        </span>
                        <h3 className="text-2xl font-serif font-bold text-emerald-900 italic">
                          Nationalpark Sommercard
                        </h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {language === 'da' 
                            ? <>Når du holder ferie i Raurisertal i Østrig, får du det fantastiske <strong>Nationalpark Sommercard</strong> med i dit ophold. Kortet giver dig gratis adgang eller store rabatter til over 60 attraktioner i regionen, herunder gratis ture med bjergbaner/lifte, adgang til svømmehaller, museer og gratis brug af offentlig transport.</>
                            : <>When you holiday in the Rauris Valley in Austria, you get the fantastic <strong>Nationalpark Sommercard</strong> included in your stay. The card gives you free access or great discounts to over 60 attractions in the region, including free rides on mountain railways/lifts, access to swimming pools, museums, and free use of public transport.</>
                          }
                        </p>
                        <a 
                          href="https://www.raurisertal.at/en/summer/nationalpark-summercard" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
                        >
                          {language === 'da' ? 'Læs mere om Sommercard her' : 'Read more about the Sommercard here'}
                          <ExternalLink size={14} />
                        </a>
                      </div>
                      <div className="md:w-1/2 w-full">
                        <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-md bg-white group/map cursor-pointer" onClick={() => window.open('https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/158df1e9-8230-41a7-a84b-38b95d04c27f.png', '_blank')}>
                          <img 
                            src="https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/158df1e9-8230-41a7-a84b-38b95d04c27f.png" 
                            alt={language === 'da' ? "Nationalpark Sommercard Områdekort" : "Nationalpark Sommercard Area Map"} 
                            className="w-full h-auto object-cover transition-transform duration-500 group-hover/map:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover/map:bg-black/10 transition-colors flex items-center justify-center">
                            <span className="bg-white/90 backdrop-blur-sm text-emerald-900 px-3 py-1.5 rounded-lg text-xs font-bold shadow-md opacity-0 group-hover/map:opacity-100 transition-opacity flex items-center gap-1.5">
                              <Maximize2 size={12} /> {language === 'da' ? 'Åbn kort i stort format' : 'Open map in large format'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Togrejse til Zell am See Card */}
                  <div className="p-6 md:p-8 bg-stone-50 rounded-3xl border border-stone-200/60">
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                      <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-800 shrink-0">
                        <i className="fa-solid fa-train text-xl" />
                      </div>
                      <div className="space-y-3 flex-1">
                        <h3 className="text-2xl font-serif font-bold text-emerald-900 italic">
                          {language === 'da' ? 'Rejs til Zell am See med tog' : 'Travel to Zell am See by train'}
                        </h3>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {language === 'da'
                            ? 'For gæster, der foretrækker at undgå fly eller bil, er det muligt at rejse fra Danmark til Zell am See med tog. Flere af togene på ruten tilbyder kørestolspladser, handicaptoiletter og mulighed for assistance ved skift undervejs. Assistance kan bestilles på forhånd gennem jernbaneselskaberne, så rejsen bliver så tryg som muligt. Der vil typisk være omkring 3 skift undervejs.'
                            : 'For guests who prefer to avoid flying or driving, it is possible to travel from Denmark to Zell am See by train. Several trains on the route offer wheelchair spaces, accessible toilets, and the option for assistance when changing trains along the way. Assistance can be booked in advance through the railway companies to make the journey as safe and comfortable as possible. There will typically be around 3 transfers along the way.'
                          }
                        </p>
                        <div className="pt-2">
                          <a 
                            href="https://www.dsb.dk/kundeservice/handicapservice/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-emerald-800 hover:text-emerald-950 font-bold text-sm transition-colors group/link"
                          >
                            <span className="underline decoration-emerald-800/30 group-hover/link:decoration-emerald-950 transition-all">
                              {language === 'da' ? 'Læs mere om DSB Handicapservice og handicapbillet her' : 'Read more about DSB Handicap Service and disabled tickets here'}
                            </span>
                            <ExternalLink size={14} className="shrink-0 text-emerald-700 group-hover/link:translate-x-0.5 transition-transform" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Detailed Hotel Cards moved to bottom of white box */}
              <div className="mt-16 pt-12 border-t border-gray-100">
                <h3 className="text-2xl font-serif font-bold text-emerald-900 mb-8 flex items-center gap-2 italic">
                  <Hotel className="text-emerald-700" />
                  {language === 'da' ? 'Vores anbefalede hoteller' : 'Our recommended hotels'}
                </h3>
                
                {detailedHotels.length > 0 ? (
                  <div className="grid grid-cols-1 gap-8 mb-10">
                    {detailedHotels.slice(0, 3).map((hotel) => (
                      <HotelCard key={hotel.id} hotel={hotel} />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 italic mb-8">
                    {language === 'da' ? 'Vi arbejder i øjeblikket på at opdatere hoteloversigten for denne destination.' : 'We are currently working on updating the hotel overview for this destination.'}
                  </p>
                )}

                <div className="text-center">
                  <Link 
                    to={`${getLocalizedPath('hotels', language)}?destination=${id}`} 
                    className="inline-flex items-center gap-2 px-8 py-4 bg-stone-50 text-emerald-900 font-bold rounded-2xl border border-emerald-100 hover:bg-emerald-50 transition-all"
                  >
                    {language === 'da' ? 'Se alle vores hoteller' : 'See all our hotels'}
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </motion.div>


            {/* Tours Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#99A18C] text-white rounded-[2rem] p-8 md:p-12 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-8">
                <CalendarDays className="text-white/80" size={32} />
                <h2 className="text-3xl font-serif font-bold italic">
                  {language === 'da' ? 'Forslag til ture og seværdigheder' : 'Suggestions for tours and sights'}
                </h2>
              </div>
              <div className="space-y-10">
                {destinationAreas.map((area, areaIdx) => (
                  <div key={areaIdx} className="space-y-4">
                    {destinationAreas.length > 1 && (
                      <h3 className="text-xl font-serif font-bold border-b border-white/20 pb-2 flex items-center gap-2 italic">
                        <span>📍</span> {area.title}
                      </h3>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {area.tours.map((tour, idx) => (
                        <div key={idx} className={`p-6 rounded-2xl backdrop-blur-sm border transition-colors ${
                          tour.isNotAccessible 
                            ? 'bg-red-950/20 border-red-900/30 text-red-100' 
                            : 'bg-white/10 border-white/10 hover:bg-white/15'
                        }`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                              tour.isNotAccessible ? 'bg-red-900/50 text-red-200' : 'bg-white/20 text-white'
                            }`}>
                              {tour.isNotAccessible ? <AlertTriangle size={16} /> : <MapPin size={16} />}
                            </div>
                            <div>
                              <h4 className="font-bold text-base flex flex-wrap items-center gap-2">
                                {tour.title}
                                {tour.isNotAccessible && (
                                  <span className="text-[10px] bg-red-900/60 text-red-200 px-2 py-0.5 rounded-full font-bold">
                                    {language === 'da' ? 'Ikke kørestolsegnet' : 'Not wheelchair accessible'}
                                  </span>
                                )}
                              </h4>
                              <p className="text-white/80 text-xs leading-relaxed mt-1.5">{tour.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Links to hotel subpages belonging to this area */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {area.hotelIds.map(hId => {
                        const h = hotels.find(hotel => hotel.id === hId);
                        if (!h) return null;
                        const translatedH = translateObject(h);
                        return (
                          <Link
                            key={hId}
                            to={getLocalizedPath('hotel-tour', language, { hotelId: hId })}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-900 text-xs font-bold rounded-xl hover:bg-stone-100 transition-all shadow-sm"
                          >
                            <span>
                              {language === 'da' ? `Se turforslag for ${translatedH.name}` : `See tour suggestions for ${translatedH.name}`}
                            </span>
                            <ArrowRight size={12} />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar / Booking CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-[2.5rem] p-8 shadow-2xl border border-emerald-50"
              >
                <div className="inline-block px-4 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                  {language === 'da' ? 'Bestilling' : 'Booking'}
                </div>
                <h3 className="text-3xl font-serif font-bold text-emerald-900 mb-6 italic">
                  {language === 'da' ? 'Klar til at rejse?' : 'Ready to travel?'}
                </h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {language === 'da' 
                    ? 'Når du har fundet din ønskede destination eller hotel, så kontakt os for et tilbud, der passer præcis til dine behov.'
                    : 'Once you have found your desired destination or hotel, contact us for a quote tailored precisely to your needs.'
                  }
                </p>
                
                <div className="p-6 bg-stone-50 rounded-2xl mb-8 border border-stone-100">
                  <p className="text-xs text-stone-500 uppercase font-bold tracking-wider mb-1">
                    {language === 'da' ? 'Prisindikation' : 'Price indication'}
                  </p>
                  <p className="text-lg font-serif font-bold text-emerald-900 mb-2 italic">
                    {language === 'da' ? 'Se de præcise fra-priser under hvert hotel' : 'See the exact starting prices under each hotel'}
                  </p>
                  <p className="text-xs text-stone-400">
                    {destination.id === 'oestrig'
                      ? (language === 'da' 
                          ? 'Prisen inkluderer hotelophold som kør-selv ferie (flyrejse er også muligt), evt. delvis forplejning samt vores personlige service.'
                          : 'The price includes hotel stay as a self-drive holiday (flight is also possible), optional partial board, and our personal service.')
                      : (language === 'da'
                          ? 'Prisen inkluderer fly, hotelophold, evt. delvis forplejning samt vores personlige service.'
                          : 'The price includes flights, hotel stay, optional partial board, and our personal service.')}
                  </p>
                </div>

                <Link 
                  to={`${getLocalizedPath('quote', language)}?destination=${destination.id}`} 
                  className="block w-full py-5 bg-emerald-900 text-white text-center font-bold rounded-2xl hover:bg-emerald-800 transition-all shadow-xl shadow-emerald-900/10 mb-4"
                >
                  {language === 'da' ? 'Udfyld tilbudsskema' : 'Fill out quote form'}
                </Link>

                <Link 
                  to={getLocalizedPath('contact', language)} 
                  className="flex items-center justify-center gap-2 w-full py-4 bg-white text-emerald-900 text-center font-bold rounded-2xl border-2 border-emerald-900 hover:bg-emerald-50 transition-all"
                >
                  {language === 'da' ? 'Stil et spørgsmål' : 'Ask a question'}
                  <ExternalLink size={16} />
                </Link>
              </motion.div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DestinationDetailPage;
